import { Box, Flex, IconButton, ScrollArea, Text } from "@radix-ui/themes";
import { CheckoutResponse } from "../api/hitl-api";
import { DisplayMoney } from "./display-money";
import { MinusIcon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useRef, useState } from "react";
import { useEffectOnce, useUpdateEffect } from "react-use";
import Countdown from "react-countdown";
import { SkuScanner } from "../views/autonomous/sku-scanner";

type ProductsTableProps = {
  lines?: CheckoutResponse["result"]["lines"];
  isEditable?: boolean;
  enableTimer?: boolean;
  duration?: number;
  onComplete: () => void;
  onUpdateQuantity?: (newValue: number, index: number) => void;
  onDelete?: (index: number) => void;
  onBarcodeScanned?: (value: string) => void;
};

export function ProductsTable({
  lines = [],
  onComplete,
  isEditable,
  onUpdateQuantity,
  onDelete,
  enableTimer = true,
  duration = 15_000,
  onBarcodeScanned,
}: ProductsTableProps) {
  const timer = useRef<NodeJS.Timeout>();
  const [timerDate, setTimerDate] = useState<Date | null>(null);

  // Setup timer to enable payment after X amount of time
  useEffectOnce(() => {
    if (!timer.current && enableTimer) {
      const newTimer = setTimeout(onComplete, duration);
      timer.current = newTimer;
      setTimerDate(new Date(Date.now() + duration));

      return () => {
        clearTimeout(timer.current);
        timer.current = undefined;
      };
    }
  });

  useUpdateEffect(() => {
    if (!enableTimer && timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
      setTimerDate(null);
    }
  }, [enableTimer]);

  return (
    <ScrollArea
      type="auto"
      className="relative border-t border-card-border !h-[calc(100vh-230px)] rounded-b-2xl"
    >
      <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
        <colgroup>
          <col width={112}></col>
          <col></col>
          <col width={176}></col>
        </colgroup>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.variant.id} className="bg-white text-black">
              <td scope="row" className="p-4 font-medium whitespace-nowrap">
                <figure className="rounded-xl border border-card-border">
                  <img
                    className="w-20 h-20 object-contain object-center rounded-xl"
                    src={line.variant.image_url || "/placeholder.jpg"}
                  />
                </figure>
              </td>
              <td className="p-4">
                <Text as="p" mb="1" size="3" color="gray" highContrast>
                  {line.variant.name}
                </Text>
                {isEditable ? (
                  <Flex align="center" gap="2">
                    <Flex align="center" className="bg-[var(--gray-3)] rounded-lg p-1">
                      <Box className="bg-white rounded-md">
                        <IconButton
                          radius="large"
                          color="gray"
                          className="!m-0 !p-1"
                          variant="ghost"
                          highContrast
                          onClick={() => {
                            onUpdateQuantity?.(Math.max(1, line.quantity - 1), index);
                          }}
                        >
                          <MinusIcon />
                        </IconButton>
                      </Box>
                      <Text as="div" align="center" weight="bold" className="w-[50px]">
                        {line.quantity}
                      </Text>
                      <Box className="bg-white rounded-md">
                        <IconButton
                          radius="large"
                          color="gray"
                          className="!m-0 !p-1"
                          variant="ghost"
                          highContrast
                          onClick={() => {
                            onUpdateQuantity?.(line.quantity + 1, index);
                          }}
                        >
                          <PlusIcon />
                        </IconButton>
                      </Box>
                    </Flex>
                    <Text as="p" weight="bold" color="gray" highContrast>
                      x <DisplayMoney value={line.variant.price_with_tax} />
                    </Text>
                  </Flex>
                ) : (
                  <Text as="p" weight="bold" color="gray" highContrast>
                    {line.quantity} x <DisplayMoney value={line.variant.price_with_tax} />
                  </Text>
                )}
              </td>
              <td className="px-6 py-4 text-right">
                <Text color="gray" highContrast size="3">
                  <DisplayMoney value={line.price_with_tax} />
                </Text>
              </td>
              {isEditable && (
                <td className="pl-2 pr-6 py-4 text-right">
                  <IconButton
                    color="red"
                    variant="soft"
                    size="4"
                    onClick={() => {
                      onDelete?.(index);
                    }}
                  >
                    <TrashIcon width={20} height={20} />
                  </IconButton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {import.meta.env.DEV && (
        <div className="absolute top-2 right-6 rounded-lg px-2 py-1 bg-white shadow-lg">
          {timerDate ? (
            <Countdown date={timerDate} />
          ) : (
            <Text color="gray" size="1">
              No timer set
            </Text>
          )}
        </div>
      )}

      {isEditable && <SkuScanner onScanned={onBarcodeScanned} />}
    </ScrollArea>
  );
}
